import { useState, useMemo, useCallback } from "react";
import {
  makeStyles,
  tokens,
  shorthands,
  Input,
  Text,
  Spinner,
  mergeClasses,
  Badge,
  Tooltip,
} from "@coreai/fluentui-react";
import {
  SearchRegular,
  AddRegular,
  PlugConnected24Regular,
  PlugDisconnected24Regular,
  Sparkle20Filled,
  ArrowRight16Regular,
  CheckmarkFilled,
  PersonRegular,
} from "@fluentui/react-icons";
import { useNodeEditorStore } from "@/stores/nodeEditorStore";
import { useConnectionStore } from "@/stores/connectionStore";
import { useNodeOperationInfo } from "@/hooks/useNodeOperationInfo";
import { useOperationManifest } from "@/hooks/useOperationManifest";
import { useServiceProviderConnector } from "@/hooks/useServiceProviderConnector";
import { useGetConnectorInfo } from "@/contexts";
import { ConnectionCreationPanel } from "./ConnectionCreationPanel";
import {
  type ConnectionReference,
  type ConnectionStatus,
  ConnectionRuntimeSource,
} from "@/types/connectionReference";
import { thinScrollbarStyles } from "@/styles/scrollbar";
import { CompactStatusDot } from "@/components/common/CompactStatusDot";
import { ConnectionDetailsPopover } from "../connections/ConnectionDetailsPopover";
import { useConnections, useRegisterCreatedConnection } from "@/hooks/useConnectionMutations";
import { useAppStore } from "@/stores/appStore";
import { isAgentConnectionId, isManagedApiConnector, isServiceProviderConnector, toShortManagedApiId, extractConnectorName, recordAgentConnectionUsage, resolveCreationConnectorName, deriveAzureAgentServiceConnectionOverrides, mapToCompactStatus, isConnectionInvalid } from "@/utils/connections";
import { getAgentReferenceProvider, getAllowedAgentProviders, readAgentModelType, readAgentHarnessType, type AgentModelProvider } from "../../workflowHelpers";
import { getAgentConnectionCreationModelType, getAgentVariantFromTypes } from "../../helpers/agentVariant";
import { buildConnectorManifest } from "@/services/manifestConnectionService";

type AgentProviderLookups = {
  providerByKey: Map<string, AgentModelProvider>;
  providerByConnectionId: Map<string, AgentModelProvider>;
};

function buildAgentProviderLookups(references: Record<string, ConnectionReference>): AgentProviderLookups {
  const providerByKey = new Map<string, AgentModelProvider>();
  const providerByConnectionId = new Map<string, AgentModelProvider>();

  for (const [refKey, ref] of Object.entries(references)) {
    if (!isAgentConnectionId(ref.api?.id)) continue;
    const provider = getAgentReferenceProvider(ref);
    if (provider === undefined) continue;

    providerByKey.set(refKey, provider);
    const connectionId = ref.connection?.id;
    if (connectionId) {
      providerByConnectionId.set(connectionId, provider);
    }
  }

  return { providerByKey, providerByConnectionId };
}

const useStyles = makeStyles({
  // Container for inline rendering - compact
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },

  // Header section - compact
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  headerIcon: {
    width: "32px",
    height: "32px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerIconSymbol: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },

  // Main content area - compact
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    overflowY: "auto",
  },

  // Search section - compact
  searchSection: {
    marginBottom: tokens.spacingVerticalXS,
  },
  searchBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ":focus-within": {
      ...shorthands.border("1px", "solid", tokens.colorBrandStroke1),
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  searchIcon: {
    color: tokens.colorNeutralForeground3,
  },

  // Create new connection - compact single line
  createNewCard: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    marginBottom: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    cursor: "pointer",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFast,
    ...shorthands.border("1px", "dashed", tokens.colorNeutralStroke2),
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
      ...shorthands.border("1px", "dashed", tokens.colorBrandStroke1),
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: "-2px",
    },
  },
  createNewIconWrapper: {
    width: "24px",
    height: "24px",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  createNewIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
  },
  createNewContent: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  createNewTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    display: "block",
  },
  createNewSubtitle: {
    display: "none", // Hidden in compact mode
  },
  createNewArrow: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },

  // Connection list section - compact
  listSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: tokens.spacingVerticalXS,
  },
  listTitle: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  connectionList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    overflowY: "auto",
    paddingRight: tokens.spacingHorizontalXS,
    ...thinScrollbarStyles,
  },

  // Connection item - compact
  connectionItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    cursor: "pointer",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFast,
    border: "1px solid transparent",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: "-2px",
    },
  },
  connectionItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    ":hover": {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },

  // Connection icon - compact
  connectionIconWrapper: {
    position: "relative",
    width: "24px",
    height: "24px",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  connectionIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  connectionIconImage: {
    width: "16px",
    height: "16px",
    objectFit: "contain" as const,
  },
  connectionIconAI: {
    color: tokens.colorPaletteTealForeground2,
  },
  statusBadge: {
    position: "absolute",
    bottom: "-2px",
    right: "-2px",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: tokens.shadow2,
  },
  statusIndicator: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
  },
  statusConnected: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
  },
  statusWarning: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
  },
  statusError: {
    backgroundColor: tokens.colorPaletteRedForeground1,
  },

  // Invalid/error connection styling - P0.2
  connectionItemInvalid: {
    opacity: 0.6,
    backgroundColor: tokens.colorNeutralBackground2,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  invalidLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightMedium,
    marginLeft: "auto",
    flexShrink: 0,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    marginLeft: "auto",
    flexShrink: 0,
  },

  // Per-user badge for dynamic/OBO connections - P0.4
  perUserBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
  },
  perUserIcon: {
    fontSize: tokens.fontSizeBase200,
  },

  // Connection info - compact
  connectionInfo: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  connectionName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  connectionMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    display: "none", // Hidden in compact mode, shown via status dot
  },
  connectionStatusText: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
  },
  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
  },

  // Selection indicator - compact
  selectionIndicator: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "transparent",
    transitionProperty: "background-color, border-color",
    transitionDuration: tokens.durationFast,
  },
  selectionIndicatorSelected: {
    backgroundColor: tokens.colorBrandBackground,
    border: `2px solid ${tokens.colorBrandBackground}`,
  },
  checkIcon: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralBackground1,
  },

  // Loading state - compact
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalS,
  },
  loadingText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },

  // Empty state - compact
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalM,
    textAlign: "center",
    gap: tokens.spacingVerticalXS,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeBase600,
    color: tokens.colorNeutralForeground4,
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  emptySubtitle: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    maxWidth: "200px",
  },
});

interface ConnectionPickerProps {
  connectorType?: string;
  connectorName?: string;
}

/**
 * ConnectionPicker - Inline component for selecting a connection
 * Shows connections filtered by the connector type of the current node
 * For Agent operations, stores connection in modelConfigurations.model1
 * For other operations, stores in host.connection
 */
export const ConnectionPicker = ({
  connectorType,
  connectorName,
}: ConnectionPickerProps = {}) => {
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreationPanel, setShowCreationPanel] = useState(false);

  const { node, parameterValues, setConnection, setSlotConnection } =
    useNodeEditorStore();

  // Get connection state from store
  const references = useConnectionStore((state) => state.references);
  const setNodeConnection = useConnectionStore(
    (state) => state.setNodeConnection,
  );
  const addConnectionReference = useConnectionStore(
    (state) => state.addConnectionReference,
  );
  const registerCreatedConnection = useRegisterCreatedConnection();

  // Used to check if an app is selected (for enabling connection fetch)
  const selectedApp = useAppStore((state) => state.selectedApp);

  const nodeType = node?.nodeData?.type ?? connectorType;
  const nodeId = node?.id ?? "";

  // Determine if this is an Agent operation (uses modelConfigurations.model1)
  const isAgentOperation = nodeType === "Agent" || nodeType === "AgentLoop";

  // Resolve connector ID using the centralized hook (handles all node types and fallbacks)
  const { apiId: resolvedApiId, operationInfo } = useNodeOperationInfo({
    nodeData: node?.nodeData,
    parameterValues,
  });

  // Get connector ID for non-agent operations
  // resolvedApiId covers managed APIs (from host.apiId / connection refs)
  // operationInfo.connectorId covers service providers (from serviceProviderConfiguration)
  const connectorId = isAgentOperation ? undefined : (resolvedApiId ?? operationInfo?.connectorId);

  // Fetch operation manifest to extract connector metadata (needed for service provider connection creation)
  const { data: operationManifest } = useOperationManifest(operationInfo);

  // Fetch the full standalone connector definition. `buildConnectorManifest`
  // prefers this over the trimmed `manifest.connector` embedded in the
  // operation manifest (which can drop auth-provider parameter sets),
  // falling back to the embedded copy while the standalone fetch is loading
  // or if it fails.
  const { data: serviceProviderConnector } = useServiceProviderConnector(connectorId ?? null);

  const connectorManifest = useMemo(
    () =>
      buildConnectorManifest({
        connectorId,
        serviceProviderConnector,
        operationManifest,
      }),
    [connectorId, serviceProviderConnector, operationManifest]
  );

  // For Agent nodes, connection is in _modelConnection_model1, not _connection
  const currentConnection = isAgentOperation
    ? (parameterValues["_modelConnection_model1"] as string | undefined)
    : (parameterValues["_connection"] as string | undefined);

  // Resolve connector display info from the operation connector first so
  // create-panel defaults don't inherit an unrelated current connection.
  const getConnectorInfo = useGetConnectorInfo();
  const connectorNameFromId = useMemo(() => {
    if (!connectorId) return undefined;
    const name = extractConnectorName(connectorId);
    return name || undefined;
  }, [connectorId]);
  const connectorInfo = useMemo(() => {
    if (connectorNameFromId) {
      const info = getConnectorInfo(connectorNameFromId);
      if (info) return info;
    }

    if (currentConnection) {
      return getConnectorInfo(currentConnection);
    }

    return undefined;
  }, [connectorNameFromId, currentConnection, getConnectorInfo]);

  // Derive agentModelType from node parameters (for Agent operations). Defaults
  // to 'AzureOpenAI' since the downstream creation panel reads this prop to
  // pick the right auth form for new agent connections.
  const agentModelType = useMemo(() => {
    if (!isAgentOperation) return undefined;
    const inputs = (node?.nodeData as Record<string, unknown> | undefined)?.inputs as
      | Record<string, unknown>
      | undefined;
    return readAgentModelType(parameterValues, inputs) ?? "AzureOpenAI";
  }, [isAgentOperation, parameterValues, node]);

  // Derive the agent variant (native/foundry/coding) so the connection list is
  // filtered to the providers valid for this agent. Managed agents additionally
  // permit GitHub Copilot connections; workflow (native) agents do not.
  const agentVariant = useMemo(() => {
    if (!isAgentOperation) return undefined;
    const inputs = (node?.nodeData as Record<string, unknown> | undefined)?.inputs as
      | Record<string, unknown>
      | undefined;
    const harnessType = readAgentHarnessType(parameterValues, inputs);
    return getAgentVariantFromTypes(agentModelType, harnessType);
  }, [isAgentOperation, parameterValues, node, agentModelType]);

  // Fetch data-plane connections for the current app.
  const { data: availableConnections, isLoading: isLoadingDataPlane } =
    useConnections(undefined, undefined, !!selectedApp);

  // Extract the connector name from the resolved API ID for filtering.
  // Prefer the operation manifest's connector id when available, since the
  // workflow-stored host.apiId can be a legacy/malformed value
  // (e.g. "/automationProjects/foo/managedApis/" with no connector tail).
  const resolvedConnectorName = useMemo(() => {
    const manifestConnectorId = operationManifest?.properties?.connector?.id;
    const manifestName = manifestConnectorId ? extractConnectorName(manifestConnectorId) : "";
    if (manifestName) return manifestName;
    if (!connectorId) return undefined;
    const fromConnectorId = extractConnectorName(connectorId);
    return fromConnectorId || undefined;
  }, [connectorId, operationManifest]);

  // Determine expected connection type from the node's operation type
  const isServiceProviderOperation = nodeType === "ServiceProvider" || nodeType === "ServiceProviderWebhook";

  // Build connection list from data-plane connections + locally-created connections from the store
  const connections = useMemo(() => {
    const result: Array<{
      name: string;
      displayName: string;
      type: string;
      status: ConnectionStatus;
      errorMessage: string | undefined;
      connectorType: string;
      isDynamic: boolean;
      iconUri: string | undefined;
      reference: ConnectionReference;
      _connectionId: string;
      _connectorId: string;
    }> = [];

    // Precompute connection.id → store reference so the inner loop stays O(1)
    // when falling back from the name-keyed lookup. Without this, every data-
    // plane row scans the full references map.
    const refsByConnectionId = new Map<string, ConnectionReference>();
    for (const ref of Object.values(references)) {
      const cid = ref.connection?.id;
      if (cid) refsByConnectionId.set(cid, ref);
    }

    if (availableConnections) {
      for (const conn of availableConnections) {
        const status = conn.status ?? "connected";
        const connectorName = extractConnectorName(conn.connectorId ?? "");

        // Look up connector info
        let info = getConnectorInfo(connectorName);
        if (!info?.iconUri) {
          info = getConnectorInfo(conn.connectorId ?? "") ?? info;
        }

        // Determine connection type from connectorId format
        const cid = conn.connectorId ?? "";
        const type = isManagedApiConnector(cid)
          ? "OAuth2"
          : isServiceProviderConnector(cid)
            ? "ConnectionString"
            : "Agent";

        // Build a ConnectionReference so the details popover and selection work.
        // Merge any metadata from the store reference (e.g. runtimeSource for OBO/Dynamic
        // connections) so the Per-user badge reflects the authored reference.
        const rawApiId = conn.connectorId ?? "";
        // Match the store reference by name first, then by underlying connection.id
        // (locally-created agent connections may be keyed differently than their
        // display name). The store carries `connectionProperties` (provider type,
        // endpoint, modelConfiguration, …) which the data-plane payload does not.
        const storeRef = references[conn.name] ?? refsByConnectionId.get(conn.id);
        const reference: ConnectionReference = {
          api: { id: isManagedApiConnector(rawApiId) ? toShortManagedApiId(rawApiId) : rawApiId },
          connection: { id: conn.id },
          connectionName: conn.name,
          ...(storeRef?.runtimeSource ? { runtimeSource: storeRef.runtimeSource } : {}),
          ...(storeRef?.connectionProperties ? { connectionProperties: storeRef.connectionProperties } : {}),
        };

        result.push({
          name: conn.name,
          displayName: conn.displayName ?? conn.name,
          type,
          status: status,
          errorMessage: undefined as string | undefined,
          connectorType: connectorName,
          isDynamic: storeRef?.runtimeSource === ConnectionRuntimeSource.Dynamic,
          iconUri: info?.iconUri,
          reference,
          _connectionId: conn.id,
          _connectorId: conn.connectorId ?? "",
        });
      }
    }

    // Merge locally-created connections from the store that aren't in the API response yet.
    // Dedupe on both the name (refKey) AND the underlying connection.id, because a store
    // reference may use a different key than the connection's display name.
    const apiKeys = new Set(result.map((c) => c.name));
    const apiConnectionIds = new Set(result.map((c) => c._connectionId).filter(Boolean));
    for (const [refKey, ref] of Object.entries(references)) {
      if (apiKeys.has(refKey)) continue;
      const refConnectionId = ref.connection?.id ?? '';
      if (refConnectionId && apiConnectionIds.has(refConnectionId)) continue;
      const apiId = ref.api?.id ?? '';
      const connectorName = extractConnectorName(apiId);
      const isSp = isServiceProviderConnector(apiId);
      const info = getConnectorInfo(refKey) ?? getConnectorInfo(connectorName);
      const type = isSp
        ? 'ConnectionString'
        : isAgentConnectionId(apiId)
          ? 'Agent'
          : 'OAuth2';
      result.push({
        name: refKey,
        displayName: ref.connectionName ?? refKey,
        type,
        status: 'connected',
        errorMessage: undefined,
        connectorType: connectorName,
        isDynamic: ref.runtimeSource === ConnectionRuntimeSource.Dynamic,
        iconUri: info?.iconUri,
        reference: ref,
        _connectionId: ref.connection?.id ?? '',
        _connectorId: apiId,
      });
    }

    return result;
  }, [availableConnections, references, getConnectorInfo]);

  const isLoading = isLoadingDataPlane;

  const agentProviderLookups = useMemo(
    () => buildAgentProviderLookups(references),
    [references],
  );

  const filteredConnections = useMemo(() => {
    let filtered = connections;

    // 1. Always filter by connection type (Agent / SP / managed)
    if (isAgentOperation) {
      // Mirror AgentTypeView / ConnectionTab: classify each connection by its
      // workflow-store reference and keep only those matching the agent's
      // variant (Foundry vs. native). When a connection has no reference at
      // all (data-plane-only entry) or its provider can't be determined we
      // leave it visible so users can still discover unmanaged connections.
      const allowed = getAllowedAgentProviders(agentVariant ?? 'native');
      filtered = filtered.filter((conn) => {
        const provider =
          agentProviderLookups.providerByKey.get(conn.name) ??
          agentProviderLookups.providerByConnectionId.get(conn._connectionId);
        if (provider === undefined) return true;
        return allowed.has(provider);
      });
    } else if (isServiceProviderOperation) {
      filtered = filtered.filter((conn) => isServiceProviderConnector(conn._connectorId));
    } else if (nodeType) {
      // Managed API operations — exclude SP and agent connections
      filtered = filtered.filter((conn) =>
        !isServiceProviderConnector(conn._connectorId)
        && conn.type !== 'Agent'
      );
    }

    // 2. Further narrow by specific connector name when available
    if (resolvedConnectorName) {
      filtered = filtered.filter((conn) =>
        conn.connectorType.toLowerCase() === resolvedConnectorName.toLowerCase()
      );
    } else if (!isAgentOperation && !isServiceProviderOperation) {
      filtered = [];
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (conn) =>
          conn.name.toLowerCase().includes(query) ||
          conn.displayName.toLowerCase().includes(query),
      );
    }

    // Sort: valid connections first, invalid/error connections at bottom
    filtered.sort((a, b) => {
      const aIsInvalid = a.status === "error" || a.status === "unauthenticated";
      const bIsInvalid = b.status === "error" || b.status === "unauthenticated";
      if (aIsInvalid && !bIsInvalid) return 1;
      if (!aIsInvalid && bIsInvalid) return -1;
      return 0;
    });

    return filtered;
  }, [connections, isAgentOperation, isServiceProviderOperation, resolvedConnectorName, nodeType, searchQuery, agentVariant, agentProviderLookups]);

  const handleSelect = (referenceKey: string) => {
    // Ensure the connection reference exists in the store
    // (data-plane connections may not be in the store yet)
    if (!references[referenceKey]) {
      const selectedConn = connections.find((c) => c.name === referenceKey);
      if (selectedConn) {
        addConnectionReference(referenceKey, selectedConn.reference);
      }
    }

    // For Agent operations, use setSlotConnection with 'model1' slot
    if (isAgentOperation) {
      setSlotConnection("model1", referenceKey);
      // Persist this pick so future Agent nodes auto-select the same connection
      // (cross-workflow when possible, via the underlying connection.id).
      const pickedRef = references[referenceKey];
      recordAgentConnectionUsage(referenceKey, pickedRef?.connection?.id);
    } else {
      setConnection(referenceKey);
    }

    // Also update the connection store mapping
    if (nodeId) {
      setNodeConnection(nodeId, referenceKey);
    }

    // Note: Since ConnectionPicker is now rendered inline in the Connection tab,
    // we don't close anything on selection. The user can navigate away using tabs.
  };

  const handleCreateComplete = (
    referenceKey: string,
    reference: ConnectionReference,
  ) => {
    registerCreatedConnection(referenceKey, reference);
    handleSelect(referenceKey);
  };

  // Get tooltip content for connection status
  const getStatusTooltip = useCallback(
    (status: ConnectionStatus, errorMessage?: string): string => {
      if (errorMessage) return errorMessage;
      switch (status) {
        case "connected":
          return "Connection active";
        case "unauthenticated":
          return "Authentication required - click to reconnect";
        case "error":
          return "Connection error - click to fix";
        default:
          return "Not configured";
      }
    },
    [],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        action();
      }
    },
    [],
  );

  const dialogTitle = "Select Connection";

  // Get subtitle based on operation type
  const dialogSubtitle = useMemo(() => {
    if (isAgentOperation) {
      return "Connection";
    }
    return nodeType ? `for ${nodeType}` : undefined;
  }, [isAgentOperation, nodeType]);

  const connectionPropertyOverrides = useMemo<Record<string, unknown> | undefined>(
    () => deriveAzureAgentServiceConnectionOverrides(resolvedConnectorName, parameterValues, node),
    [resolvedConnectorName, parameterValues, node],
  );

  // Show creation panel
  if (showCreationPanel) {
    const creationAgentModelType = agentVariant
      ? getAgentConnectionCreationModelType(agentModelType, agentVariant)
      : undefined;
    const creationConnectorName = resolveCreationConnectorName({
      isAgentOperation,
      agentModelType: creationAgentModelType,
      connectorDisplayName: connectorInfo?.displayName,
      fallbackName: resolvedConnectorName ?? connectorName,
      nodeType,
    });

    return (
      <div className={styles.container}>
        <ConnectionCreationPanel
          {...(connectorId ? { connectorId } : {})}
          connectorType={nodeType}
          {...(creationConnectorName ? { connectorName: creationConnectorName } : {})}
          {...(connectorInfo?.iconUri ? { connectorIcon: connectorInfo.iconUri } : {})}
          {...(connectorManifest ? { connectorManifest } : {})}
          {...(creationAgentModelType
            ? { agentModelType: creationAgentModelType }
            : {})}
          {...(connectionPropertyOverrides ? { connectionPropertyOverrides } : {})}
          onComplete={handleCreateComplete}
          onBack={() => setShowCreationPanel(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          {isAgentOperation ? (
            <Sparkle20Filled className={styles.headerIconSymbol} />
          ) : (
            <PlugConnected24Regular className={styles.headerIconSymbol} />
          )}
        </div>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{dialogTitle}</h3>
          {dialogSubtitle && (
            <Text className={styles.subtitle}>
              {isAgentOperation && (
                <Sparkle20Filled fontSize={12} />
              )}
              {dialogSubtitle}
            </Text>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* Search */}
        <div className={styles.searchSection}>
          <Input
            className={styles.searchBox}
            contentBefore={<SearchRegular className={styles.searchIcon} />}
            placeholder="Search connections..."
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            appearance="filled-darker"
          />
        </div>

        {/* Create new connection card */}
        <div
          className={styles.createNewCard}
          onClick={() => setShowCreationPanel(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => handleKeyDown(e, () => setShowCreationPanel(true))}
        >
          <div className={styles.createNewIconWrapper}>
            <AddRegular className={styles.createNewIcon} />
          </div>
          <div className={styles.createNewContent}>
            <Text className={styles.createNewTitle}>Create new connection</Text>
            <Text className={styles.createNewSubtitle}>
              Set up a new service connection
            </Text>
          </div>
          <ArrowRight16Regular className={styles.createNewArrow} />
        </div>

        {/* Connection list section */}
        <div className={styles.listSection}>
          <div className={styles.listHeader}>
            <Text className={styles.listTitle}>
              Available Connections
            </Text>
            {filteredConnections.length > 0 && (
              <Badge appearance="filled" color="informative">
                {filteredConnections.length}
              </Badge>
            )}
          </div>

          {/* Connection list */}
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner size="medium" />
              <Text className={styles.loadingText}>Loading connections...</Text>
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className={styles.emptyState}>
              <PlugDisconnected24Regular className={styles.emptyIcon} />
              <Text className={styles.emptyTitle}>No connections found</Text>
              <Text className={styles.emptySubtitle}>
                {searchQuery
                  ? "Try a different search term or create a new connection"
                  : "Create a new connection to get started"}
              </Text>
            </div>
          ) : (
            <div className={styles.connectionList}>
              {filteredConnections.map((connection) => {
                const isSelected = currentConnection === connection.name;
                const isInvalid = isConnectionInvalid(connection.status);
                const compactStatus = mapToCompactStatus(connection.status);
                const statusTooltip = getStatusTooltip(
                  connection.status,
                  connection.errorMessage,
                );

                const connectionItem = (
                  <div
                    className={mergeClasses(
                      styles.connectionItem,
                      isSelected && styles.connectionItemSelected,
                      isInvalid && styles.connectionItemInvalid,
                    )}
                    onClick={() => handleSelect(connection.name)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => handleSelect(connection.name))
                    }
                    aria-selected={isSelected}
                  >
                    {/* Connection icon with status badge */}
                    <div className={styles.connectionIconWrapper}>
                      {connection.iconUri ? (
                        <img
                          src={connection.iconUri}
                          alt=""
                          className={styles.connectionIconImage}
                          aria-hidden="true"
                        />
                      ) : isAgentOperation ? (
                        <Sparkle20Filled
                          className={mergeClasses(
                            styles.connectionIcon,
                            styles.connectionIconAI,
                          )}
                        />
                      ) : (
                        <PlugConnected24Regular
                          className={styles.connectionIcon}
                        />
                      )}
                      <div className={styles.statusBadge}>
                        <CompactStatusDot
                          status={compactStatus}
                          size="small"
                          showTooltip={!isInvalid}
                          tooltipContent={statusTooltip}
                        />
                      </div>
                    </div>

                    {/* Connection info */}
                    <div className={styles.connectionInfo}>
                      <Text className={styles.connectionName}>
                        {connection.displayName}
                      </Text>
                    </div>

                    {/* Per-user badge for dynamic/OBO connections - P0.4 */}
                    {connection.isDynamic && (
                      <Tooltip content="Each user authenticates with their own credentials at runtime" relationship="description">
                        <span className={styles.perUserBadge}>
                          <PersonRegular className={styles.perUserIcon} />
                          Per-user
                        </span>
                      </Tooltip>
                    )}

                    {/* Connection details popover - P1.1 */}
                    <ConnectionDetailsPopover
                      referenceKey={connection.name}
                      reference={connection.reference}
                      displayName={connection.displayName}
                      status={connection.status}
                      errorMessage={connection.errorMessage}
                      connectorType={connection.connectorType}
                    />

                    {/* Status indicator and invalid label */}
                    <div className={styles.statusContainer}>
                      {isInvalid && (
                        <Text className={styles.invalidLabel}>Invalid</Text>
                      )}

                      {/* Selection indicator */}
                      <div
                        className={mergeClasses(
                          styles.selectionIndicator,
                          isSelected && styles.selectionIndicatorSelected,
                        )}
                      >
                        {isSelected && (
                          <CheckmarkFilled className={styles.checkIcon} />
                        )}
                      </div>
                    </div>
                  </div>
                );

                // Wrap invalid connections with tooltip showing error message
                return isInvalid ? (
                  <Tooltip
                    key={connection.name}
                    content={statusTooltip}
                    relationship="description"
                  >
                    {connectionItem}
                  </Tooltip>
                ) : (
                  <div key={connection.name}>{connectionItem}</div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ConnectionPicker.displayName = "ConnectionPicker";
